import { useQueryClient, useMutation } from '@tanstack/react-query';
import { queryClient } from '../lib/query-client';
import { queryKeys } from '../lib/query-keys';
import { getSongMetadata, getYouTubeSuggestions, submitSong, getSongs as getSongsApi } from '../api/http-req';
import type { SongMetadata, FinalSong } from '../interfaces/final-song';
import type { YouTubeSuggestions } from '../interfaces/youtube-suggestions';
import type { Artwork } from '../interfaces/artwork';

// Imperative reads. getSongMetadata fires from a click handler (open the
// SONG_METADATA modal) and getYouTubeSuggestions from a modal mount effect that
// also orchestrates modal state — neither is a declarative component data
// dependency, so we use fetchQuery (cache + dedup, orchestration unchanged)
// rather than useQuery.

/** Hook returning an imperative, cached fetcher for a song's metadata. */
export function useSongMetadataFetcher() {
  const qc = useQueryClient();
  return (id: string): Promise<SongMetadata> =>
    qc.fetchQuery({
      queryKey: queryKeys.songs.metadata(id),
      queryFn: () => getSongMetadata(id),
    });
}

/** Module-level cached fetch for YouTube suggestions (called from an effect). */
export function fetchYouTubeSuggestions(searchStr: string): Promise<YouTubeSuggestions[]> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.songs.youtubeSuggestions(searchStr),
    queryFn: () => getYouTubeSuggestions(searchStr),
  });
}

// Cached fetch for the song-search endpoint (search/list/songs), used by the
// nav-bar type-ahead autocomplete and the /similar/ URL lookup. Re-typed or
// repeated queries within staleTime become cache hits (no network); concurrent
// duplicate lookups collapse into one request; each lookup logs under 'query'.
// NOTE: this is caching, not debouncing — every distinct keystroke string still
// fetches once. Type-ahead staleness is short-lived, so 5 minutes is plenty.
export function fetchSongsByQuery(searchStr: string): Promise<Artwork[]> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.songs.search(searchStr),
    queryFn: () => getSongsApi(searchStr),
    staleTime: 5 * 60 * 1000,
  });
}

// Submit-song mutation (YouTubeSuggestions modal). Wraps the unchanged api.
// submitSong (two sequential POSTs); call site keeps its toasts + store write.
export function useSubmitSong() {
  return useMutation({
    mutationKey: ['song', 'submit'],
    mutationFn: (song: FinalSong) => submitSong(song),
  });
}
